import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabase';

function ContactsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State Management
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Load accounts from apollo_accounts table
  useEffect(() => {
    loadAccounts();
  }, [user?.id]);

  const loadAccounts = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('apollo_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setAccounts(data || []);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = 
      account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.domain?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesIndustry = industryFilter === 'all' || account.industry === industryFilter;
    
    return matchesSearch && matchesIndustry;
  });

  // Pagination
  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIdx, startIdx + itemsPerPage);

  // Get unique industries for filter
  const industries = [...new Set(accounts.map(a => a.industry).filter(Boolean))];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <DashboardLayout title="Accounts" currentPath="/contacts">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header with Search and Filters */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-2xl font-headline-bold text-foreground mb-4">Company Accounts</h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <Input
              placeholder="Search by name, email, or domain..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              icon="Search"
            />
            
            <Select
              value={industryFilter}
              onChange={(e) => {
                setIndustryFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Industries</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </Select>

            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                icon={<Icon name="RefreshCw" />}
                onClick={loadAccounts}
                full
              >
                Refresh
              </Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-3">
            Showing {filteredAccounts.length} of {accounts.length} accounts
          </p>
        </div>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-red-600 flex items-center gap-2"
          >
            <Icon name="AlertCircle" size={20} />
            {error}
          </motion.div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading accounts...</p>
            </div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-border rounded-lg p-12 text-center"
          >
            <Icon name="Building2" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-headline-semibold text-foreground mb-2">No Accounts Found</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || industryFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'No company accounts yet'}
            </p>
          </motion.div>
        ) : (
          <>
            {/* Accounts Table */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card border border-border rounded-lg overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Company</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Domain</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Industry</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Location</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Added</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {paginatedAccounts.map((account, index) => (
                        <motion.tr
                          key={account.id}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.05 }}
                          className="border-b border-border hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {account.logo_url && (
                                <img
                                  src={account.logo_url}
                                  alt={account.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              )}
                              <div>
                                <p className="font-medium text-foreground">{account.name || 'N/A'}</p>
                                {account.email && (
                                  <p className="text-xs text-muted-foreground">{account.email}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <a
                              href={`https://${account.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-sm"
                            >
                              {account.domain || 'N/A'}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">
                              {account.industry || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">
                              {account.city && account.country
                                ? `${account.city}, ${account.country}`
                                : account.location || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {account.phone ? (
                              <a
                                href={`tel:${account.phone}`}
                                className="text-primary hover:underline text-sm"
                              >
                                {account.phone}
                              </a>
                            ) : (
                              <span className="text-sm text-muted-foreground">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">
                              {formatDate(account.created_at)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedAccount(account);
                                setShowDetailModal(true);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 hover:bg-primary/20 text-primary rounded text-sm font-medium transition-colors"
                            >
                              <Icon name="Eye" size={14} />
                              View
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  icon={<Icon name="ChevronLeft" size={16} />}
                >
                  Previous
                </Button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  icon={<Icon name="ChevronRight" size={16} />}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Account Detail Modal */}
        <AnimatePresence>
          {showDetailModal && selectedAccount && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDetailModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto"
              >
                {/* Modal Header */}
                <div className="bg-muted/50 border-b border-border p-6 flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    {selectedAccount.logo_url && (
                      <img
                        src={selectedAccount.logo_url}
                        alt={selectedAccount.name}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div>
                      <h3 className="text-xl font-headline-bold text-foreground">
                        {selectedAccount.name}
                      </h3>
                      {selectedAccount.domain && (
                        <p className="text-sm text-muted-foreground">{selectedAccount.domain}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Column 1 */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Industry</label>
                      <p className="text-foreground mt-1">{selectedAccount.industry || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Location</label>
                      <p className="text-foreground mt-1">
                        {selectedAccount.city && selectedAccount.country
                          ? `${selectedAccount.city}, ${selectedAccount.state}, ${selectedAccount.country}`
                          : selectedAccount.location || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Founded</label>
                      <p className="text-foreground mt-1">{selectedAccount.founded_year || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Email</label>
                      <a href={`mailto:${selectedAccount.email}`} className="text-primary hover:underline mt-1">
                        {selectedAccount.email || 'N/A'}
                      </a>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Phone</label>
                      <p className="text-foreground mt-1">
                        {selectedAccount.phone ? (
                          <a href={`tel:${selectedAccount.phone}`} className="text-primary hover:underline">
                            {selectedAccount.phone}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Website</label>
                      <a
                        href={selectedAccount.website_url || `https://${selectedAccount.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline mt-1"
                      >
                        Visit Website
                      </a>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">LinkedIn</label>
                      {selectedAccount.linkedin_url ? (
                        <a
                          href={selectedAccount.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline mt-1"
                        >
                          View Profile
                        </a>
                      ) : (
                        <p className="text-muted-foreground mt-1">N/A</p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Added</label>
                      <p className="text-foreground mt-1">{formatDate(selectedAccount.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="border-t border-border p-6 flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </DashboardLayout>
  );
}

export default ContactsPage;
